// src/services/api.js

const API = process.env.REACT_APP_API_URL;

export function getToken()        { return localStorage.getItem('tripdata_token'); }
export function setToken(token)   { localStorage.setItem('tripdata_token', token); }
export function removeToken()     { localStorage.removeItem('tripdata_token'); }

async function request(method, path, body = null) {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    'Accept':       'application/json',
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : null,
  });

  const data = await res.json();

  // Handle token rotation
  const newToken = res.headers.get('X-New-Token');
  if (newToken) setToken(newToken);

  return data;
}

export const auth = {
  login:  (email, password)               => request('POST', '/auth/login',    { email, password }),
  google: (id_token)                      => request('POST', '/auth/google',   { id_token }),
  me:     ()                              => request('GET',  '/auth/me'),
  forgot: (email)                         => request('POST', '/auth/forgot',   { email }),
  reset:  (token, password)               => request('POST', '/auth/reset',    { token, password }),
};

export const trips = {
  list: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request('GET', `/trips${qs ? '?' + qs : ''}`);
  },
  get:       (id)       => request('GET', `/trips/${id}`),
  update:    (id, data) => request('PUT', `/trips/${id}`, data),
  waypoints: (id)       => request('GET', `/trips/${id}/waypoints`),
};

export const reasons = {
  list: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request('GET', `/reasons${qs ? '?' + qs : ''}`);
  },
};

export const vehicles = {
  list: () => request('GET', '/vehicles'),
};

export const reports = {
  summary: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request('GET', `/reports/summary${qs ? '?' + qs : ''}`);
  },
  sars: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request('GET', `/reports/sars${qs ? '?' + qs : ''}`);
  },
};

export const expenses = {
  summary: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request('GET', `/expenses/summary${qs ? '?' + qs : ''}`);
  },
};

export const zones = {
  list:   ()           => request('GET',    '/zones'),
  create: (data)       => request('POST',   '/zones', data),
  update: (id, data)   => request('PUT',    `/zones/${id}`, data),
  delete: (id)         => request('DELETE',  `/zones/${id}`),
};

// Download export — fetches all trips for the date range, generates report client-side
// using the same format as the native app (tripReportExport.js)
// format: 'csv' | 'pdf'
// params: { date_from, date_to, userName?, vehicleName? }
export async function downloadExport(format, params = {}) {
  const {
    date_from, date_to, vehicle_id = null,
    userName = '', vehicleName = '',
    vehicleReg = '', startingOdometer = null,
  } = params;

  // Fetch ALL completed trips for the date range (no pagination — export needs full set)
  // vehicle_id scopes the logbook to one car, which SARS requires
  const allTrips = [];
  let page = 1;
  let totalPages = 1;
  const vehicleQs = vehicle_id ? `&vehicle_id=${vehicle_id}` : '';

  do {
    const res = await request('GET', `/trips?status=completed&date_from=${date_from}&date_to=${date_to}${vehicleQs}&page=${page}`);
    if (!res.success) throw new Error(res.error || 'Failed to fetch trips');
    const data = res.data || [];
    allTrips.push(...data);
    totalPages = res.meta?.pagination?.total_pages || 1;
    page++;
  } while (page <= totalPages);
 
  if (allTrips.length === 0) throw new Error('No trips found for this period');
 
  const { exportTripReport } = await import('../utils/tripReportExport');
  exportTripReport(allTrips, {
    format, userName, vehicleName, vehicleReg, startingOdometer,
    dateFrom: date_from, dateTo: date_to,
  });
}

export const vouchers = {
  redeem: (code) => request('POST', '/vouchers/redeem', { code }),
};

export const payments = {
  pricing:   ()     => request('GET',  '/payments/pricing'),
  initiate:  (plan) => request('POST', '/payments/initiate', { plan }),
  claim:     (id)   => request('POST', '/payments/claim', { id }),
  verifyIap: (data) => request('POST', '/iap/verify', data),
  current: () => request('GET', '/payments/current'),
};

export const user = {
  update:         (data)                          => request('PUT', '/user/profile',  data),
  changePassword: (current_password, new_password) =>
    request('PUT', '/user/password', { current_password, new_password }),
  getSchedule: () => request('GET', '/user/work-schedule'),
  getPeriods:  () => request('GET', '/user/periods'),
};

export const dealer = {
  dashboard:    (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request('GET', `/dealers/portal/dashboard${qs ? '?' + qs : ''}`);
  },
  listVouchers: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request('GET', `/dealers/portal/vouchers${qs ? '?' + qs : ''}`);
  },
  issueCode:  () => request('POST', '/dealers/portal/issue'),
  recallCode: (code) => request('POST', '/dealers/portal/recall', { code }),
};

export const admin = {
  listPayments:   (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request('GET', `/admin/payments${qs ? '?' + qs : ''}`);
  },
  confirmPayment: (id)    => request('POST', `/admin/payments/${id}/confirm`),
  cancelPayment:  (id)    => request('POST', `/admin/payments/${id}/cancel`),

  listUsers: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request('GET', `/admin/users${qs ? '?' + qs : ''}`);
  },
  assignBatch:   (userId, batchId) => request('POST', `/admin/users/${userId}/assign-batch`, { batch_id: batchId }),
  setTier:       (userId, tier, pro_expires_at) => request('POST', `/admin/users/${userId}/set-tier`, { tier, pro_expires_at }),

  listBatches:   ()           => request('GET',  '/admin/vouchers/batches'),
  createBatch:   (data)       => request('POST', '/admin/vouchers/batches', data),
  getBatch:      (id)         => request('GET',  `/admin/vouchers/batches/${id}`),
  generateCodes: (id, count)  => request('POST', `/admin/vouchers/batches/${id}/generate`, { count }),
  listCodes:     (id)         => request('GET',  `/admin/vouchers/batches/${id}/codes`),

  getSettings:   ()           => request('GET', '/config'),
  updateSetting: (key, value) => request('PUT', `/config/${key}`, { value }),
};